import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import Axios from 'axios';
import * as Routes from '@/modules/routes';
import QueryKeys from '@/utils/QueryKeys';

export interface Program {
  id: number;
  title: string;
  copyrights: string;
  code: string;
  numLicenses: number;
  publishStatus: string;
  archived: boolean;
  subscribers: string[];
}

interface RawProgram {
  id: number;
  full_title_with_edition: string;
  all_copyright_years: string[];
  code: string;
  num_licenses: number;
  publish_status: string;
  archived: boolean;
  active_subscriber_names?: string[];
}

const fetchPrograms = async (custom: boolean): Promise<RawProgram[]> => {
  const only = [
    'id', 'code', 'all_copyright_years', 'full_title_with_edition',
    'num_licenses', 'publish_status', 'archived',
  ];

  const includes: string[] = [];

  if (custom) {
    includes.push('active_subscribers');
    only.push('active_subscriber_names');
  }

  const params = {
    ...(custom ? { custom: true } : { standard: true }),
    ...(includes.length > 0 ? { includes } : {}),
    only,
  };

  const response = await Axios.get(Routes.plato_api_programs_path(params));

  return response.data.data;
};

const transformProgram = (raw: RawProgram): Program => ({
  id: raw.id,
  title: raw.full_title_with_edition,
  copyrights: raw.all_copyright_years.join(', '),
  code: raw.code,
  numLicenses: raw.num_licenses,
  publishStatus: raw.publish_status,
  archived: raw.archived,
  subscribers: raw.active_subscriber_names || [],
});

// AIDEV-NOTE: React Query hook replacing useProgramsTableData's raw useEffect/Axios pattern.
// Splits programs into archived/non-archived for the toggle.
const useContentPrograms = (custom: boolean) => {
  const { data: rawPrograms = [], isLoading } = useQuery({
    queryKey: [QueryKeys.ContentPrograms, custom],
    queryFn: () => fetchPrograms(custom),
  });

  const { programs, archivedPrograms } = useMemo(() => {
    const active: Program[] = [];
    const archived: Program[] = [];

    rawPrograms.forEach((raw) => {
      const program = transformProgram(raw);

      if (program.archived) {
        archived.push(program);
      } else {
        active.push(program);
      }
    });

    return { programs: active, archivedPrograms: archived };
  }, [rawPrograms]);

  return { programs, archivedPrograms, isLoading };
};

export default useContentPrograms;
